import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * Editorial intro splash — shown once per browser session on first load.
 * Minimal, considered, on-brand: cream background, serif wordmark reveals,
 * a single hairline rule draws across, then the whole composition eases out.
 *
 * To replace with a real logo later, swap the <TextMark /> block with
 * <img src={logoUrl} /> — the surrounding animation choreography stays.
 */

const EASE = [0.16, 1, 0.3, 1];

const TextMark = () => (
  <div className="flex flex-col items-center">
    {/* Big monogram */}
    <div className="overflow-hidden">
      <motion.div
        initial={{ y: "110%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1.1, delay: 0.15, ease: EASE }}
        style={{ fontFamily: "'Times New Roman', Times, serif" }}
        className="text-neutral-950 text-[18vw] sm:text-[14vw] lg:text-[11vw] leading-[0.85] font-normal tracking-tight"
      >
        A&amp;A
      </motion.div>
    </div>

    {/* Hairline rule draws in */}
    <motion.div
      initial={{ scaleX: 0 }}
      animate={{ scaleX: 1 }}
      transition={{ duration: 0.9, delay: 0.95, ease: EASE }}
      className="h-px bg-neutral-950 w-[70vw] max-w-[520px] mt-8 origin-left"
    />

    {/* Wordmark + overline */}
    <div className="mt-6 text-center">
      <div className="overflow-hidden">
        <motion.p
          initial={{ y: "110%" }}
          animate={{ y: 0 }}
          transition={{ duration: 0.9, delay: 1.2, ease: EASE }}
          className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light tracking-tight text-neutral-950"
        >
          Attry <span className="italic">&amp;</span> Associates
        </motion.p>
      </div>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 1.6, ease: EASE }}
        className="text-[10px] sm:text-[11px] tracking-[0.32em] uppercase text-neutral-500 mt-3"
      >
        Chartered Accountants · Est. 2017
      </motion.p>
    </div>
  </div>
);

const SplashIntro = () => {
  const [visible, setVisible] = useState(() => {
    if (typeof window === "undefined") return false;
    return !sessionStorage.getItem("attry-intro-seen");
  });

  useEffect(() => {
    if (!visible) return;
    // Total choreography ~ 2.6s, then exit animation
    const t = setTimeout(() => {
      sessionStorage.setItem("attry-intro-seen", "1");
      setVisible(false);
    }, 2600);
    return () => clearTimeout(t);
  }, [visible]);

  // Lock scroll while splash is visible
  useEffect(() => {
    if (visible) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [visible]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="splash"
          data-testid="splash-intro"
          initial={{ opacity: 1 }}
          exit={{ y: "-100%" }}
          transition={{ duration: 1.0, ease: EASE }}
          className="fixed inset-0 z-[100] bg-[var(--bone)] flex items-center justify-center"
        >
          <TextMark />
          {/* Tiny corner details for editorial feel */}
          <div className="absolute top-6 left-6 text-[10px] tracking-[0.32em] uppercase text-neutral-500">
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              — {new Date().toLocaleDateString("en-GB", { month: "long", year: "numeric" })}
            </motion.span>
          </div>
          <div className="absolute bottom-6 right-6 text-[10px] tracking-[0.32em] uppercase text-neutral-500">
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Chandigarh · India
            </motion.span>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SplashIntro;
